import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Card,
  Dropdown,
  Form,
  Modal,
  Button,
  Spinner,
  Table,
} from "react-bootstrap";
import { Link, useSearchParams } from "react-router";
import { AlertTriangle, Check, CornerDownRight, MoreHorizontal, X } from "react-feather";
import { apiErrorMessage, useDelete, useGetLazy, usePost } from "../../api";
import { usePermissions } from "../../hooks";
import {
  DomainReviewAction,
  DomainReviewEntity,
  DomainReviewItemStatus,
  IDomainReviewBrand,
  IDomainReviewBrandDomainRow,
  IDomainReviewBrandSuggestions,
  IDomainReviewItem,
  IDomainReviewItemRef,
  IDomainReviewItemsResponse,
  IDomainReviewLinkRow,
  IDomainReviewPromoRow,
  IDomainReviewResponse,
  IDomainReviewRunSummary,
  IDomainReviewRunsResponse,
  IDomainReviewStageResponse,
  KindredPublishStatus,
  PermissionAction,
  PermissionResource,
} from "../../interfaces";
import {
  EmptyState,
  FilterBar,
  FilterField,
  PageHeader,
  PageToolbar,
  ToolbarButton,
  ToolbarMenu,
  ToolbarMenuItem,
} from "../../components/ui";
import { BrandTypeahead, ChangeLogTable } from "../../components/workbench";

const BASE = "domain-review";
const ROW_LIMIT = 200;
/** Matches the backend: bigger changes go to the background worker. */
const INLINE_MAX = 10;
const POLL_MS = 3000;

const ACTIONS: {
  action: DomainReviewAction;
  label: string;
  hint: string;
}[] = [
  {
    action: DomainReviewAction.RemoveDomain,
    label: "Remove domain",
    hint: "Deletes this domain from the selected rows. Coupons, offers and campaigns also get the Domains override so the next import doesn't put it back. Brand links and registry rows are deleted outright.",
  },
  {
    action: DomainReviewAction.ReplaceDomain,
    label: "Replace domain…",
    hint: "Swaps this domain for another on the selected rows, with the same Domains override as removing.",
  },
  {
    action: DomainReviewAction.ChangeBrand,
    label: "Change brand…",
    hint: "Moves the selected rows to another brand. Coupon and offer hashes are recomputed; a campaign's domain rows move with it.",
  },
];

const ENTITY_LABEL: Record<DomainReviewEntity, string> = {
  [DomainReviewEntity.Coupon]: "Coupon",
  [DomainReviewEntity.Offer]: "Offer",
  [DomainReviewEntity.Campaign]: "Campaign",
  [DomainReviewEntity.BrandLink]: "Brand link",
  [DomainReviewEntity.BrandDomain]: "Registry",
};

const ITEM_STATUS: Record<DomainReviewItemStatus, { label: string; variant: string }> = {
  pending: { label: "Ready", variant: "primary" },
  processing: { label: "Applying", variant: "info" },
  updated: { label: "Updated", variant: "success" },
  no_change: { label: "No change", variant: "light" },
  skipped: { label: "Skipped", variant: "warning" },
  failed: { label: "Failed", variant: "danger" },
  cancelled: { label: "Cancelled", variant: "secondary" },
  invalid: { label: "Not applicable", variant: "secondary" },
};

const PUBLISH_LABEL: Record<number, string> = {
  [KindredPublishStatus.Draft]: "Draft",
  [KindredPublishStatus.AwaitingApproval]: "Awaiting",
  [KindredPublishStatus.Approved]: "Approved",
  [KindredPublishStatus.Deactivated]: "Deactivated",
};

const refKey = (r: IDomainReviewItemRef) => `${r.entity}:${r.id}`;
const fmtDate = (s: string | null) => (s ? new Date(s).toLocaleDateString() : "—");
const cachedAgo = (iso: string) => {
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  return `${Math.floor(mins / 60)} h ${mins % 60} min ago`;
};
const describeLink = (l: IDomainReviewLinkRow) =>
  `• ${l.campaignId ? `campaign "${l.campaignName ?? l.campaignId}"${l.source ? ` (${l.source})` : ""}` : "brand-only link"}` +
  ` — ${l.validated ? "validated" : "not validated"}` +
  (l.campaignId ? (l.dealLive ? ", live" : l.suspended ? ", suspended" : ", not live") : "") +
  (l.matchesLookup ? "" : `, spelled ${l.domain} (never matched)`);

const listLinks = (links: IDomainReviewLinkRow[]) =>
  links.length ? links.map(describeLink).join("\n") : "• (not among the rows listed on this page)";

// Mirrors the checkout lookup in the campaigns API (CouponCodeReadRepository / OffersReadRepository).
const explainCheckout = (r: IDomainReviewPromoRow, domain: string, links: IDomainReviewLinkRow[]): string => {
  const brand = r.brandName ?? r.brandId;
  const table = r.code !== null ? "coupon_domains" : "offer_domains";
  const ownRows = r.directDomains.filter((d) => !d.startsWith("www."));
  const validated = links.filter((l) => l.validated && l.matchesLookup);
  const gates =
    "\n\nThe lookup also requires: approved, inside its valid dates, a public affiliate source, enabled for the shopper's country, and allowed by the partner's prohibited-content setting" +
    (r.code === null ? ", and an offer test inside the test window." : ".");
  if (r.servedAtCheckout) {
    return ownRows.length
      ? `Returned at checkout on ${domain}: it has its own ${table} row (${ownRows.join(", ")}).${gates}`
      : `Returned at checkout on ${domain} through ${brand}'s validated campaign_domains rows. Campaign status doesn't matter here, only "validated":\n${listLinks(validated)}${gates}`;
  }
  const own = r.directDomains.length
    ? `Its own ${table} row is spelled ${r.directDomains.join(", ")}, which the extension never sends (it strips www.).`
    : `It has no ${table} row for ${domain}.`;
  return `Not returned at checkout on ${domain}. ${own} ${brand} has no validated campaign_domains row for it either:\n${listLinks(links)}\n\nIt would be returned if it got its own ${table} row, or if one of those rows were validated.`;
};

// Mirrors the cashback-deal lookup: live campaigns on the domain, then the brand's coupons by source.
const explainDeal = (r: IDomainReviewPromoRow, domain: string, links: IDomainReviewLinkRow[]): string => {
  const brand = r.brandName ?? r.brandId;
  const live = links.filter((l) => l.campaignId && l.dealLive && l.matchesLookup);
  if (r.servedWithDeal) {
    return `Shown with ${brand}'s cashback deal on ${domain}. The deal lookup finds the brand's live campaign and attaches the brand's coupons from the same source or a roaming affiliate (${r.source}); coupon_domains and "validated" play no part:\n${listLinks(live)}`;
  }
  if (live.length === 0) {
    const campaigns = links.filter((l) => l.campaignId);
    return `Not shown with a cashback deal on ${domain}: ${brand} has no live campaign there. The deal lookup needs a campaign_domains row on a campaign that is published, inside its dates and not suspended (validated doesn't matter):\n${listLinks(campaigns)}`;
  }
  return `Not shown with ${brand}'s cashback deal: its live campaign on ${domain} is from ${Array.from(new Set(live.map((l) => l.source))).join(", ")}, and ${r.source} is neither that source nor a roaming affiliate.`;
};

const isDone = (r: IDomainReviewRunSummary) =>
  ["completed", "failed", "cancelled"].includes(r.status);

// ── Page ─────────────────────────────────────────────────────────────────────

export const DomainReview: React.FC = () => {
  const { can, isAdmin } = usePermissions();
  const canFix =
    isAdmin || can(PermissionAction.CreateAny, PermissionResource.DomainReview);

  const [searchParams, setSearchParams] = useSearchParams();
  const [domainInput, setDomainInput] = useState(searchParams.get("domain") ?? "");
  const [includeInactive, setIncludeInactive] = useState(false);
  const [brandFilter, setBrandFilter] = useState<string | null>(null);
  const [mismatchOnly, setMismatchOnly] = useState(false);
  const [data, setData] = useState<IDomainReviewResponse | null>(null);
  const [reference, setReference] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<Map<string, IDomainReviewItemRef>>(new Map());
  const [fix, setFix] = useState<{ action: DomainReviewAction; refs: IDomainReviewItemRef[] } | null>(null);
  const [runs, setRuns] = useState<IDomainReviewRunSummary[]>([]);
  const [runDetail, setRunDetail] = useState<string | null>(null);
  const [logRefresh, setLogRefresh] = useState(0);

  const { execute: fetchReview, loading, error } = useGetLazy<IDomainReviewResponse>();
  const { execute: fetchRuns } = useGetLazy<IDomainReviewRunsResponse>();
  const { execute: deleteApi } = useDelete<{ domain: string; cleared: number }>();
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(
    async (opts: { domain?: string; force?: boolean; brand?: string | null } = {}) => {
      const domain = (opts.domain ?? data?.domain ?? domainInput).trim();
      if (!domain) return;
      const brand = opts.brand !== undefined ? opts.brand : brandFilter;
      const params = new URLSearchParams({
        domain,
        includeInactive: String(includeInactive),
        limit: String(ROW_LIMIT),
      });
      if (brand) params.set("brandId", brand);
      if (opts.force) params.set("force", "true");
      const r = await fetchReview(`${BASE}?${params}`).catch(() => null);
      if (!r) return;
      setData((prev) => {
        // Keep the reviewer's reference brands while they stay on the same domain.
        if (!prev || prev.domain !== r.domain) setReference(new Set(r.suggestedBrandIds));
        return r;
      });
      setSelected(new Map());
      setSearchParams({ domain: r.domain }, { replace: true });
      fetchRuns(`${BASE}/runs?domain=${encodeURIComponent(r.domain)}&pageSize=10`)
        .then((x) => setRuns(x.runs))
        .catch(() => null);
    },
    [brandFilter, data?.domain, domainInput, fetchReview, fetchRuns, includeInactive, setSearchParams]
  );

  useEffect(() => {
    if (domainInput) load({ domain: domainInput });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetCache = async () => {
    if (!data) return;
    try {
      const r = await deleteApi(`${BASE}/cache?domain=${encodeURIComponent(data.domain)}`);
      setNotice(`Cleared ${r.cleared} cached view${r.cleared === 1 ? "" : "s"} of ${data.domain}; reading it fresh.`);
    } catch (e) {
      setNotice(apiErrorMessage(e));
    }
    await load({ force: true });
  };

  const search = () => {
    setBrandFilter(null);
    setData(null);
    load({ domain: domainInput, brand: null });
  };

  const isMismatch = (brandId: string | null | undefined) =>
    reference.size > 0 && !!brandId && !reference.has(brandId);

  const toggle = (ref: IDomainReviewItemRef, on: boolean) =>
    setSelected((prev) => {
      const next = new Map(prev);
      if (on) next.set(refKey(ref), ref);
      else next.delete(refKey(ref));
      return next;
    });
  const toggleAll = (refs: IDomainReviewItemRef[], on: boolean) =>
    setSelected((prev) => {
      const next = new Map(prev);
      for (const r of refs) {
        if (on) next.set(refKey(r), r);
        else next.delete(refKey(r));
      }
      return next;
    });

  const visible = <T,>(rows: T[], brandOf: (r: T) => string | null) =>
    mismatchOnly ? rows.filter((r) => isMismatch(brandOf(r))) : rows;

  const mismatchCount = useMemo(() => {
    if (!data) return 0;
    const n = (ids: (string | null)[]) => ids.filter((b) => isMismatch(b)).length;
    return (
      n(data.brandDomains.map((r) => r.brandId)) +
      n(data.brandLinks.rows.map((r) => r.brandId)) +
      n(data.campaigns.rows.map((r) => r.campaignBrandId ?? r.brandId)) +
      n(data.coupons.rows.map((r) => r.brandId)) +
      n(data.offers.rows.map((r) => r.brandId))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, reference]);

  const brandIdsOf = (refs: IDomainReviewItemRef[]): string[] => {
    if (!data) return [];
    const byKey = new Map<string, string>();
    for (const r of data.coupons.rows) byKey.set(`${DomainReviewEntity.Coupon}:${r.id}`, r.brandId);
    for (const r of data.offers.rows) byKey.set(`${DomainReviewEntity.Offer}:${r.id}`, r.brandId);
    for (const r of data.campaigns.rows) {
      if (r.campaignId) byKey.set(`${DomainReviewEntity.Campaign}:${r.campaignId}`, r.campaignBrandId ?? r.brandId);
    }
    for (const r of data.brandLinks.rows) byKey.set(`${DomainReviewEntity.BrandLink}:${r.id}`, r.brandId);
    for (const r of data.brandDomains) byKey.set(`${DomainReviewEntity.BrandDomain}:${r.key}`, r.brandId);
    return Array.from(new Set(refs.map((ref) => byKey.get(refKey(ref))).filter((b): b is string => !!b)));
  };

  const openFix = (action: DomainReviewAction, refs?: IDomainReviewItemRef[]) =>
    setFix({ action, refs: refs ?? Array.from(selected.values()) });

  const rowMenu = (ref: IDomainReviewItemRef, disabled?: Partial<Record<DomainReviewAction, string>>) =>
    canFix ? (
      <RowFixMenu onPick={(a) => openFix(a, [ref])} disabled={disabled} />
    ) : null;

  const linksByBrand = useMemo(() => {
    const m = new Map<string, IDomainReviewLinkRow[]>();
    for (const l of [...(data?.brandLinks.rows ?? []), ...(data?.campaigns.rows ?? [])]) {
      m.set(l.brandId, [...(m.get(l.brandId) ?? []), l]);
    }
    return m;
  }, [data]);

  const sectionProps = { selected, toggle, toggleAll, isMismatch, canFix, rowMenu };
  const hidden = (all: number, shown: number) => (mismatchOnly ? all - shown : 0);

  return (
    <div>
      <PageHeader
        title="Domain Review"
        description="Everything Kindred links to one domain: brand registry, brand links, campaigns, coupons and offers. Rows whose brand isn't one of the reference brands are flagged as likely misplaced."
      />

      <FilterBar
        onSearch={search}
        activeCount={includeInactive ? 1 : 0}
        searchLabel="Review domain"
        summary={data ? `Reviewing ${data.domain} (${data.variants.join(", ")})` : undefined}
      >
        <FilterField label="Domain" hint="A host or URL. Matches the bare and www. spellings exactly." md={6}>
          <Form.Control
            size="sm"
            placeholder="example.com"
            value={domainInput}
            onChange={(e) => setDomainInput(e.target.value)}
          />
        </FilterField>
        <FilterField label="Coupons & offers" hint="Off: only live ones (approved and within their valid dates)." md={3}>
          <Form.Check
            type="switch"
            id="dr-inactive"
            label="Include inactive"
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.target.checked)}
          />
        </FilterField>
      </FilterBar>

      {error && <Alert variant="danger">{apiErrorMessage(error)}</Alert>}
      {notice && (
        <Alert variant="info" dismissible onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}

      {!data && !loading && (
        <EmptyState
          icon="bi-globe"
          title="Enter a domain to review"
          message="Results are cached per domain for an hour; the page always says whether you're looking at a cached view."
        />
      )}
      {loading && !data && (
        <div className="text-center py-5">
          <Spinner animation="border" size="sm" />
        </div>
      )}

      {data && (
        <>
          <PageToolbar>
            <ToolbarMenu
              label="Fix selected"
              icon="bi-wrench"
              variant="primary"
              count={selected.size}
              disabled={!canFix || selected.size === 0}
            >
              {ACTIONS.map((a) => (
                <ToolbarMenuItem
                  key={a.action}
                  label={a.label}
                  hint={a.hint}
                  onClick={() => openFix(a.action)}
                />
              ))}
            </ToolbarMenu>
            <ToolbarButton
              label="Clear selection"
              hint="Unselect every row."
              icon="bi-x-square"
              onClick={() => setSelected(new Map())}
              disabled={selected.size === 0}
            />
            <ToolbarButton
              label={mismatchOnly ? "Show all rows" : "Mismatches only"}
              hint="Hide rows whose brand is one of the reference brands."
              icon="bi-funnel"
              onClick={() => setMismatchOnly((m) => !m)}
              count={mismatchCount}
            />
            <ToolbarButton
              label="Read fresh"
              hint="Reads this view from the database now and replaces its cached copy."
              icon="bi-arrow-clockwise"
              onClick={() => load({ force: true })}
              disabled={loading}
            />
            <ToolbarButton
              label="Reset cache"
              hint={`Deletes every cached view of ${data.domain} (all filters), then reads it fresh. Cached views otherwise last ${data.cacheMinutes} minutes, and are cleared automatically when a change touches this domain.`}
              icon="bi-trash"
              onClick={resetCache}
              disabled={loading}
            />
            {data.fromCache ? (
              <Badge
                bg="warning"
                text="dark"
                title={`Read from the database at ${new Date(data.cachedAt).toLocaleString()}`}
              >
                Cached view · read {cachedAgo(data.cachedAt)} · expires in{" "}
                {Math.max(0, data.cacheMinutes - Math.round((Date.now() - new Date(data.cachedAt).getTime()) / 60_000))} min
              </Badge>
            ) : (
              <Badge bg="success" title={new Date(data.cachedAt).toLocaleString()}>
                Fresh from the database
              </Badge>
            )}
            {loading && <Spinner animation="border" size="sm" />}
          </PageToolbar>

          <BrandsCard
            brands={data.brands}
            reference={reference}
            setReference={setReference}
            brandFilter={brandFilter}
            onFilter={(b) => {
              setBrandFilter(b);
              load({ brand: b });
            }}
          />

          <BrandDomainsSection
            rows={visible(data.brandDomains, (r) => r.brandId)}
            hidden={hidden(data.brandDomains.length, visible(data.brandDomains, (r) => r.brandId).length)}
            {...sectionProps}
          />
          <LinksSection
            title="Brand links"
            description="campaign_domains rows with no campaign. Every coupon and offer of the brand matches the domain through them."
            entity={DomainReviewEntity.BrandLink}
            section={data.brandLinks}
            rows={visible(data.brandLinks.rows, (r) => r.brandId)}
            hidden={hidden(data.brandLinks.rows.length, visible(data.brandLinks.rows, (r) => r.brandId).length)}
            {...sectionProps}
          />
          <LinksSection
            title="Campaigns"
            description="campaign_domains rows that belong to a campaign. They also link the campaign's brand, so its coupons and offers match too. Ended or deleted campaigns are hidden unless inactive rows are included."
            entity={DomainReviewEntity.Campaign}
            section={data.campaigns}
            rows={visible(data.campaigns.rows, (r) => r.campaignBrandId ?? r.brandId)}
            hidden={hidden(data.campaigns.rows.length, visible(data.campaigns.rows, (r) => r.campaignBrandId ?? r.brandId).length)}
            {...sectionProps}
          />
          <PromoSection
            title="Coupons"
            entity={DomainReviewEntity.Coupon}
            section={data.coupons}
            rows={visible(data.coupons.rows, (r) => r.brandId)}
            hidden={hidden(data.coupons.rows.length, visible(data.coupons.rows, (r) => r.brandId).length)}
            domain={data.domain}
            linksByBrand={linksByBrand}
            {...sectionProps}
          />
          <PromoSection
            title="Offers"
            entity={DomainReviewEntity.Offer}
            section={data.offers}
            rows={visible(data.offers.rows, (r) => r.brandId)}
            hidden={hidden(data.offers.rows.length, visible(data.offers.rows, (r) => r.brandId).length)}
            domain={data.domain}
            linksByBrand={linksByBrand}
            {...sectionProps}
          />

          <RunsCard runs={runs} onOpen={setRunDetail} />
          <Card className="mb-3">
            <Card.Header className="py-2 small fw-semibold">
              Change log for {data.domain}
              <span className="text-muted fw-normal ms-2">
                Every change recorded against this domain, from any part of the app.
              </span>
              <Link target="_blank" rel="noopener noreferrer" to={`/kindred/change-log?domain=${encodeURIComponent(data.domain)}`} className="ms-2 fw-normal">
                Open full log
              </Link>
            </Card.Header>
            <Card.Body className="p-2">
              <ChangeLogTable
                filter={{ domain: data.domain }}
                refreshKey={logRefresh}
                emptyTitle={`No changes recorded for ${data.domain} yet`}
              />
            </Card.Body>
          </Card>
        </>
      )}

      {fix && data && (
        <FixModal
          domain={data.domain}
          action={fix.action}
          refs={fix.refs}
          brandIds={brandIdsOf(fix.refs)}
          onHide={(changed) => {
            setFix(null);
            if (changed) {
              load({ force: true });
              setLogRefresh((n) => n + 1);
            }
          }}
        />
      )}
      <RunDetailModal runId={runDetail} onHide={() => setRunDetail(null)} />
    </div>
  );
};

// ── Brands ───────────────────────────────────────────────────────────────────

const BrandsCard: React.FC<{
  brands: IDomainReviewBrand[];
  reference: Set<string>;
  setReference: (s: Set<string>) => void;
  brandFilter: string | null;
  onFilter: (brandId: string | null) => void;
}> = ({ brands, reference, setReference, brandFilter, onFilter }) => {
  const sorted = [...brands].sort(
    (a, b) => Number(reference.has(b.brandId)) - Number(reference.has(a.brandId)) ||
      Number(b.registered) - Number(a.registered)
  );
  return (
    <Card className="mb-3">
      <Card.Header className="py-2 small fw-semibold">
        Brands on this domain
        <span className="text-muted fw-normal ms-2">
          Tick the brands this domain really belongs to; everything else is flagged.
          Defaults to the registered brands.
        </span>
      </Card.Header>
      <Card.Body className="p-0">
        {sorted.length === 0 ? (
          <div className="text-muted small text-center py-3">Nothing links to this domain.</div>
        ) : (
          <Table size="sm" hover responsive className="mb-0 small align-middle">
            <thead>
              <tr>
                <th title="Reference brand">Ref</th>
                <th>Brand</th>
                <th>Registry</th>
                <th className="text-end">Campaigns</th>
                <th className="text-end">Brand links</th>
                <th className="text-end" title="direct / only through a brand link">Coupons</th>
                <th className="text-end" title="direct / only through a brand link">Offers</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {sorted.map((b) => (
                <tr key={b.brandId} className={reference.has(b.brandId) ? "" : "table-warning"}>
                  <td>
                    <Form.Check
                      checked={reference.has(b.brandId)}
                      onChange={(e) => {
                        const next = new Set(reference);
                        if (e.target.checked) next.add(b.brandId);
                        else next.delete(b.brandId);
                        setReference(next);
                      }}
                    />
                  </td>
                  <td>
                    <Link target="_blank" rel="noopener noreferrer" to={`/kindred/brands/${b.brandId}`}>{b.name ?? b.brandId}</Link>
                  </td>
                  <td>
                    {b.registered ? (
                      <Badge bg={b.registeredValidated ? "success" : "secondary"}>
                        {b.registeredValidated ? "Validated" : "Registered"}
                      </Badge>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                  <td className="text-end">{b.counts.campaigns}</td>
                  <td className="text-end">{b.counts.brandLinks}</td>
                  <td className="text-end">
                    {b.counts.couponsDirect} / {b.counts.couponsInherited}
                  </td>
                  <td className="text-end">
                    {b.counts.offersDirect} / {b.counts.offersInherited}
                  </td>
                  <td className="text-end">
                    <Button
                      size="sm"
                      variant="link"
                      className="p-0"
                      onClick={() => onFilter(brandFilter === b.brandId ? null : b.brandId)}
                    >
                      {brandFilter === b.brandId ? "Show all brands" : "Only this brand"}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card.Body>
    </Card>
  );
};

// ── Sections ─────────────────────────────────────────────────────────────────

interface ISectionProps {
  selected: Map<string, IDomainReviewItemRef>;
  toggle: (ref: IDomainReviewItemRef, on: boolean) => void;
  toggleAll: (refs: IDomainReviewItemRef[], on: boolean) => void;
  isMismatch: (brandId: string | null | undefined) => boolean;
  canFix: boolean;
  rowMenu: (
    ref: IDomainReviewItemRef,
    disabled?: Partial<Record<DomainReviewAction, string>>
  ) => React.ReactNode;
}

const SectionCard: React.FC<{
  title: string;
  description: string;
  count: string;
  refs: IDomainReviewItemRef[];
  head: React.ReactNode;
  children: React.ReactNode;
  hidden?: number;
} & Pick<ISectionProps, "selected" | "toggleAll" | "canFix">> = ({
  title,
  description,
  count,
  refs,
  head,
  children,
  hidden = 0,
  selected,
  toggleAll,
  canFix,
}) => {
  const all = refs.length > 0 && refs.every((r) => selected.has(refKey(r)));
  return (
    <Card className="mb-3">
      <Card.Header className="py-2 small">
        <span className="fw-semibold">{title}</span>
        <Badge bg="secondary" className="ms-2">{count}</Badge>
        <span className="text-muted ms-2">{description}</span>
      </Card.Header>
      <Card.Body className="p-0">
        {refs.length === 0 ? (
          <div className="text-muted small text-center py-3">
            {hidden > 0
              ? `No mismatches. ${hidden} row${hidden === 1 ? "" : "s"} on reference brands hidden by "Mismatches only".`
              : "None."}
          </div>
        ) : (
          <Table size="sm" hover responsive className="mb-0 small align-middle">
            <thead>
              <tr>
                <th style={{ width: 28 }}>
                  {canFix && (
                    <Form.Check checked={all} onChange={(e) => toggleAll(refs, e.target.checked)} />
                  )}
                </th>
                {head}
                <th style={{ width: 32 }} />
              </tr>
            </thead>
            <tbody>{children}</tbody>
          </Table>
        )}
      </Card.Body>
    </Card>
  );
};

const SelectCell: React.FC<{
  ref_: IDomainReviewItemRef;
} & Pick<ISectionProps, "selected" | "toggle" | "canFix">> = ({ ref_, selected, toggle, canFix }) => (
  <td>
    {canFix && (
      <Form.Check
        checked={selected.has(refKey(ref_))}
        onChange={(e) => toggle(ref_, e.target.checked)}
      />
    )}
  </td>
);

const BrandCell: React.FC<{ id: string | null; name: string | null; mismatch: boolean }> = ({
  id,
  name,
  mismatch,
}) => (
  <td>
    {id ? <Link target="_blank" rel="noopener noreferrer" to={`/kindred/brands/${id}`}>{name ?? id}</Link> : "—"}
    {mismatch && (
      <Badge bg="danger" className="ms-1" title="Not one of the reference brands">
        mismatch
      </Badge>
    )}
  </td>
);

const BrandDomainsSection: React.FC<{ rows: IDomainReviewBrandDomainRow[]; hidden: number } & ISectionProps> = ({
  rows,
  hidden,
  ...p
}) => {
  const refs = rows.map((r) => ({ entity: DomainReviewEntity.BrandDomain, id: r.key }));
  return (
    <SectionCard
      title="Brand registry"
      description="brand_domains rows — which brand Kindred believes owns the domain."
      count={String(rows.length + hidden)}
      hidden={hidden}
      refs={refs}
      head={
        <>
          <th>Brand</th>
          <th>Domain</th>
          <th>Country</th>
          <th>Validated</th>
          <th>Source</th>
          <th>Updated</th>
        </>
      }
      {...p}
    >
      {rows.map((r, i) => {
        const mismatch = p.isMismatch(r.brandId);
        return (
          <tr key={r.key} className={mismatch ? "table-danger" : ""}>
            <SelectCell ref_={refs[i]} {...p} />
            <BrandCell id={r.brandId} name={r.brandName} mismatch={mismatch} />
            <td>{r.domain}</td>
            <td>{r.countryCode ?? "—"}</td>
            <td>{r.isValidated ? <Badge bg="success">yes</Badge> : "no"}</td>
            <td className="text-muted">{r.validationSource ?? "—"}</td>
            <td className="text-muted">{fmtDate(r.updatedAt)}</td>
            <td>{p.rowMenu(refs[i])}</td>
          </tr>
        );
      })}
    </SectionCard>
  );
};

const LinksSection: React.FC<{
  title: string;
  description: string;
  entity: DomainReviewEntity.Campaign | DomainReviewEntity.BrandLink;
  section: IDomainReviewResponse["campaigns"];
  rows: IDomainReviewLinkRow[];
  hidden: number;
} & ISectionProps> = ({ title, description, entity, section, rows, hidden, ...p }) => {
  const isCampaign = entity === DomainReviewEntity.Campaign;
  // A campaign is one target even when it carries both spellings.
  const unique = isCampaign
    ? rows.filter((r, i) => rows.findIndex((x) => x.campaignId === r.campaignId) === i)
    : rows;
  const refs = unique.map((r) => ({ entity, id: isCampaign ? r.campaignId! : r.id }));
  return (
    <SectionCard
      title={title}
      description={
        section.truncated
          ? `${description} Showing the first ${section.rows.length} rows, brands with the fewest links first.`
          : description
      }
      count={String(section.total)}
      refs={refs}
      hidden={hidden}
      head={
        <>
          {isCampaign && <th>Campaign</th>}
          <th>Brand</th>
          <th>Domain</th>
          <th title="The checkout lookup follows a brand's campaign_domains rows only when validated.">Checkout</th>
          {isCampaign && <th title="The cashback-deal lookup needs the campaign published, in date and not suspended; validated doesn't matter.">Cashback deal</th>}
          <th className="text-end" title="Coupons / offers of this brand returned at checkout only through its validated links">
            Depends on it
          </th>
        </>
      }
      {...p}
    >
      {unique.map((r, i) => {
        const brandId = isCampaign ? r.campaignBrandId ?? r.brandId : r.brandId;
        const mismatch = p.isMismatch(brandId);
        const drift = isCampaign && r.campaignBrandId && r.campaignBrandId !== r.brandId;
        const domains = isCampaign
          ? rows.filter((x) => x.campaignId === r.campaignId).map((x) => x.domain)
          : [r.domain];
        return (
          <tr key={refs[i].id} className={mismatch ? "table-danger" : ""}>
            <SelectCell ref_={refs[i]} {...p} />
            {isCampaign && (
              <td>
                <Link target="_blank" rel="noopener noreferrer" to={`/kindred/deals/${r.campaignId}`}>{r.campaignName ?? r.campaignId}</Link>
                {r.source && <span className="text-muted ms-1">({r.source})</span>}
              </td>
            )}
            <BrandCell
              id={brandId}
              name={isCampaign ? r.campaignBrandName ?? r.brandName : r.brandName}
              mismatch={mismatch}
            />
            <td>
              {domains.join(", ")}
              {drift && (
                <Badge
                  bg="warning"
                  text="dark"
                  className="ms-1"
                  title={`campaign_domains.brand_id is ${r.brandName ?? r.brandId}, not the campaign's brand`}
                >
                  brand drift
                </Badge>
              )}
            </td>
            <td>
              <ServedBadge
                label={r.validated ? "validated" : "not validated"}
                served={r.validated && r.matchesLookup}
                title={
                  !r.matchesLookup
                    ? `Spelled ${r.domain}; the extension sends the bare domain, so no lookup ever matches this row.`
                    : r.validated
                      ? `Validated: the checkout lookup returns ${r.brandName ?? "this brand"}'s coupons and offers for this domain through this row.`
                      : "Not validated: the checkout lookup ignores this row."
                }
              />
            </td>
            {isCampaign && (
              <td>
                <ServedBadge
                  label={r.dealLive ? "live" : r.suspended ? "suspended" : "not live"}
                  served={r.dealLive && r.matchesLookup}
                  title={
                    r.dealLive
                      ? "Published, in date and not suspended: the cashback-deal lookup finds this campaign for the domain."
                      : `The cashback-deal lookup skips it (status ${r.publishedStatus != null ? PUBLISH_LABEL[r.publishedStatus] : "unknown"}${r.suspended ? ", suspended" : ""}${r.endDate ? `, ends ${fmtDate(r.endDate)}` : ""}).`
                  }
                />
              </td>
            )}
            <td className="text-end">
              {r.inheritedCoupons} / {r.inheritedOffers}
            </td>
            <td>{p.rowMenu(refs[i])}</td>
          </tr>
        );
      })}
    </SectionCard>
  );
};

const PromoSection: React.FC<{
  title: string;
  entity: DomainReviewEntity.Coupon | DomainReviewEntity.Offer;
  section: IDomainReviewResponse["coupons"];
  rows: IDomainReviewPromoRow[];
  hidden: number;
  domain: string;
  linksByBrand: Map<string, IDomainReviewLinkRow[]>;
} & ISectionProps> = ({ title, entity, section, rows, hidden, domain, linksByBrand, ...p }) => {
  const isCoupon = entity === DomainReviewEntity.Coupon;
  const refs = rows.map((r) => ({ entity, id: r.id }));
  const brandOnly =
    "Matches only through a brand link; fix the link, or change this row's brand.";
  return (
    <SectionCard
      title={title}
      description={
        section.truncated
          ? `Showing ${section.rows.length} of ${section.total}, brands with the fewest rows on this domain first. Use "Only this brand" to narrow.`
          : "Linked directly, or through a brand link on the same brand."
      }
      count={String(section.total)}
      refs={refs}
      hidden={hidden}
      head={
        <>
          <th>{isCoupon ? "Code" : "Summary"}</th>
          <th>Brand</th>
          <th>Link</th>
          <th title="What the live lookups in the campaigns API return for this domain. Hover each for why.">Served</th>
          <th>Other domains</th>
          <th>Status</th>
          <th>Valid to</th>
          <th>Source</th>
        </>
      }
      {...p}
    >
      {rows.map((r, i) => {
        const mismatch = p.isMismatch(r.brandId);
        const others = r.allDomains.filter((d) => !r.directDomains.includes(d));
        const direct = r.directDomains.length > 0;
        return (
          <tr key={r.id} className={mismatch ? "table-danger" : ""}>
            <SelectCell ref_={refs[i]} {...p} />
            <td className="text-truncate" style={{ maxWidth: 260 }} title={r.summary ?? ""}>
              <Link target="_blank" rel="noopener noreferrer" to={`/kindred/${isCoupon ? "coupons" : "offers"}/${r.id}`}>
                {isCoupon ? r.code : r.summary ?? r.id}
              </Link>
            </td>
            <BrandCell id={r.brandId} name={r.brandName} mismatch={mismatch} />
            <td>
              {direct && (
                <Badge bg="primary" title={`Own ${isCoupon ? "coupon_domains" : "offer_domains"} row: ${r.directDomains.join(", ")}`}>
                  own row
                </Badge>
              )}
              {r.inherited && (
                <Badge
                  bg="light"
                  text="dark"
                  className="ms-1 border"
                  title={`${r.brandName ?? r.brandId} has campaign_domains rows for ${domain}:\n${listLinks(linksByBrand.get(r.brandId) ?? [])}`}
                >
                  <CornerDownRight size={10} /> brand link
                </Badge>
              )}
              {(r.overridesFlag & 16) === 16 && (
                <Badge bg="info" className="ms-1" title="Domains override set; imports leave its domains alone">
                  override
                </Badge>
              )}
            </td>
            <td className="text-nowrap">
              <ServedBadge
                label="Checkout"
                served={r.servedAtCheckout}
                title={explainCheckout(r, domain, linksByBrand.get(r.brandId) ?? [])}
              />
              {r.servedWithDeal !== null && (
                <ServedBadge
                  label="Cashback deal"
                  served={r.servedWithDeal}
                  title={explainDeal(r, domain, linksByBrand.get(r.brandId) ?? [])}
                />
              )}
            </td>
            <td className="text-muted text-truncate" style={{ maxWidth: 200 }} title={others.join(", ")}>
              {others.length ? others.join(", ") : "—"}
            </td>
            <td>{PUBLISH_LABEL[r.publishedStatus] ?? r.publishedStatus}</td>
            <td>{fmtDate(r.validTo)}</td>
            <td className="text-muted">{r.source ?? "—"}</td>
            <td>
              {p.rowMenu(
                refs[i],
                direct
                  ? undefined
                  : {
                      [DomainReviewAction.RemoveDomain]: brandOnly,
                      [DomainReviewAction.ReplaceDomain]: brandOnly,
                    }
              )}
            </td>
          </tr>
        );
      })}
    </SectionCard>
  );
};

const ServedBadge: React.FC<{ label: string; served: boolean; title: string }> = ({
  label,
  served,
  title,
}) => (
  <Badge
    bg={served ? "success" : "light"}
    text={served ? undefined : "dark"}
    className={`me-1 ${served ? "" : "border"}`}
    title={title}
  >
    {served ? <Check size={10} /> : <X size={10} />} {label}
  </Badge>
);

const RowFixMenu: React.FC<{
  onPick: (a: DomainReviewAction) => void;
  disabled?: Partial<Record<DomainReviewAction, string>>;
}> = ({ onPick, disabled }) => (
  <Dropdown align="end">
    <Dropdown.Toggle size="sm" variant="link" className="p-0 text-muted" bsPrefix="x">
      <MoreHorizontal size={16} />
    </Dropdown.Toggle>
    {/* Fixed positioning escapes the table-responsive overflow that clipped the menu. */}
    <Dropdown.Menu style={{ width: "22rem" }} popperConfig={{ strategy: "fixed" }} renderOnMount>
      {ACTIONS.map((a) => (
        <ToolbarMenuItem
          key={a.action}
          label={a.label}
          hint={disabled?.[a.action] ?? a.hint}
          disabled={!!disabled?.[a.action]}
          onClick={() => onPick(a.action)}
        />
      ))}
    </Dropdown.Menu>
  </Dropdown>
);

// ── Fix: preview → confirm → result ──────────────────────────────────────────

const FixModal: React.FC<{
  domain: string;
  action: DomainReviewAction;
  refs: IDomainReviewItemRef[];
  brandIds: string[];
  onHide: (changed: boolean) => void;
}> = ({ domain, action, refs, brandIds, onHide }) => {
  const [newDomain, setNewDomain] = useState("");
  const [suggestions, setSuggestions] = useState<IDomainReviewBrandSuggestions[] | null>(null);
  const [brand, setBrand] = useState({ id: "", name: "" });
  const [preview, setPreview] = useState<IDomainReviewStageResponse | null>(null);
  const [run, setRun] = useState<IDomainReviewRunSummary | null>(null);
  const [items, setItems] = useState<IDomainReviewItem[]>([]);
  const [reason, setReason] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Once confirm was sent the run may have started even if the response was lost.
  const [applyAttempted, setApplyAttempted] = useState(false);

  const { execute: post } = usePost<any>();
  const { execute: get } = useGetLazy<any>();
  const { execute: del } = useDelete<unknown>();

  // Replacement candidates: the selected brands' own campaign domains, best-validated first.
  useEffect(() => {
    if (action !== DomainReviewAction.ReplaceDomain || brandIds.length === 0) return;
    get(`${BASE}/suggest-domains?domain=${encodeURIComponent(domain)}&brandIds=${brandIds.join(",")}`)
      .then((r: IDomainReviewBrandSuggestions[]) => {
        setSuggestions(r);
        const top = r.length === 1 ? r[0].suggestions[0] : undefined;
        if (top && top.validated > 0) setNewDomain((current) => current || top.domain);
      })
      .catch(() => setSuggestions([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action, domain, brandIds.join(",")]);

  const meta = ACTIONS.find((a) => a.action === action)!;
  const queued = preview?.run.queuedCount ?? 0;
  const typed = queued > INLINE_MAX;
  const hasPromoBrandMove =
    action === DomainReviewAction.ChangeBrand &&
    (preview?.items ?? []).some(
      (i) =>
        i.status === "pending" &&
        (i.entity === DomainReviewEntity.Coupon || i.entity === DomainReviewEntity.Offer)
    );

  const stage = async () => {
    setBusy(true);
    setErr(null);
    try {
      const res: IDomainReviewStageResponse = await post(`${BASE}/stage`, {
        domain,
        action,
        newDomain: action === DomainReviewAction.ReplaceDomain ? newDomain : undefined,
        newBrandId: action === DomainReviewAction.ChangeBrand ? brand.id : undefined,
        items: refs,
      });
      setPreview(res);
      setItems(res.items);
      setConfirmText(res.run.queuedCount > INLINE_MAX ? "" : String(res.run.queuedCount));
    } catch (e) {
      setErr(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const loadItems = useCallback(
    async (runId: string) => {
      const all: IDomainReviewItem[] = [];
      for (let page = 1; ; page++) {
        const r: IDomainReviewItemsResponse = await get(
          `${BASE}/runs/${runId}/items?pageSize=500&page=${page}`
        );
        all.push(...r.items);
        if (all.length >= r.total || r.items.length === 0) break;
      }
      setItems(all);
    },
    [get]
  );

  const apply = async () => {
    if (!preview) return;
    setApplyAttempted(true);
    setBusy(true);
    setErr(null);
    try {
      const r: IDomainReviewRunSummary = await post(`${BASE}/runs/${preview.run.runId}/start`, {
        reason: reason.trim(),
        confirmCount: Number(confirmText),
      });
      setRun(r);
      await loadItems(r.runId);
    } catch (e) {
      setErr(
        `${apiErrorMessage(e)}. The change may have started anyway; close this and check "Changes made on this domain" before trying again.`
      );
    } finally {
      setBusy(false);
    }
  };

  // Background runs: poll until the worker finishes.
  useEffect(() => {
    if (!run || isDone(run)) return;
    const t = setInterval(async () => {
      const r: IDomainReviewRunSummary | null = await get(`${BASE}/runs/${run.runId}`).catch(() => null);
      if (!r) return;
      setRun(r);
      if (isDone(r)) await loadItems(r.runId);
    }, POLL_MS);
    return () => clearInterval(t);
  }, [run, get, loadItems]);

  const close = async () => {
    // An unconfirmed preview has written nothing; discard it. After a confirm, never.
    if (preview && !run && !applyAttempted) {
      await del(`${BASE}/runs/${preview.run.runId}`).catch(() => null);
    }
    onHide(!!run || applyAttempted);
  };

  const canPreview =
    action === DomainReviewAction.RemoveDomain ||
    (action === DomainReviewAction.ReplaceDomain && newDomain.trim().length > 3) ||
    (action === DomainReviewAction.ChangeBrand && !!brand.id);
  const canApply =
    !!preview && !run && queued > 0 && reason.trim().length >= 5 && Number(confirmText) === queued;

  return (
    <Modal show onHide={close} size="xl" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title className="h6">
          {meta.label.replace("…", "")} on {domain} — {refs.length} row{refs.length === 1 ? "" : "s"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted small">{meta.hint}</p>

        {!preview && (
          <>
            {action === DomainReviewAction.ReplaceDomain && (
              <Form.Group className="mb-3">
                <Form.Label className="small fw-semibold">Replace {domain} with</Form.Label>
                <Form.Control
                  size="sm"
                  placeholder="correct-domain.com"
                  value={newDomain}
                  onChange={(e) => setNewDomain(e.target.value)}
                />
                <Form.Text className="text-muted">
                  Stored as typed (lower-cased). Prefer the bare domain: the extension strips www. before looking up, so a www. spelling never matches.
                </Form.Text>
                <DomainSuggestions
                  suggestions={suggestions}
                  selected={newDomain.trim().toLowerCase()}
                  onPick={setNewDomain}
                />
              </Form.Group>
            )}
            {action === DomainReviewAction.ChangeBrand && (
              <Form.Group className="mb-3">
                <Form.Label className="small fw-semibold">Move to brand</Form.Label>
                <BrandTypeahead
                  brandId={brand.id}
                  brandName={brand.name}
                  onChange={(id, name) => setBrand({ id, name })}
                />
              </Form.Group>
            )}
            <Alert variant="light" className="small mb-0">
              Preview reads the current rows and shows exactly what would change. Nothing is
              written until you confirm.
            </Alert>
          </>
        )}

        {preview && (
          <>
            {!run && (
              <Alert variant={queued ? "info" : "warning"} className="small">
                {queued} of {items.length} row{items.length === 1 ? "" : "s"} can be changed.
                {queued > INLINE_MAX
                  ? " This change runs in the background, a few rows at a time."
                  : " This change is applied as soon as you confirm."}
              </Alert>
            )}
            {hasPromoBrandMove && !run && (
              <Alert variant="warning" className="small">
                <AlertTriangle size={14} className="me-1" />
                Moving a coupon or offer recomputes its <code>coupon_hash</code> /{" "}
                <code>offer_hash</code> (code | brand | summary). Its metadata row moves to the new
                hash, and coupon targeting moves to the new brand, unless another row still uses the
                old key. Imported rows come back under the old brand unless the importer's
                merchant→brand mapping is fixed.
              </Alert>
            )}
            {run && (
              <Alert variant={run.status === "completed" ? "success" : run.status === "failed" ? "danger" : "info"} className="small">
                {isDone(run) ? `Change ${run.status}` : "Running…"}: {run.counts.updated} updated,{" "}
                {run.counts.noChange} unchanged, {run.counts.skipped} skipped, {run.counts.failed} failed
                {run.errorMessage ? ` — ${run.errorMessage}` : ""}
                {!isDone(run) && <Spinner animation="border" size="sm" className="ms-2" />}
              </Alert>
            )}
            <ItemsTable items={items} />
            {!run && queued > 0 && (
              <div className="mt-3">
                <Form.Group className="mb-2">
                  <Form.Label className="small fw-semibold">Reason</Form.Label>
                  <Form.Control
                    size="sm"
                    value={reason}
                    placeholder="e.g. coupons from the wrong merchant feed"
                    onChange={(e) => setReason(e.target.value)}
                  />
                  <Form.Text className="text-muted">
                    Goes into the audit comment with your name and this change's id.
                  </Form.Text>
                </Form.Group>
                {typed && (
                  <Form.Group>
                    <Form.Label className="small fw-semibold">
                      Type {queued} to confirm
                    </Form.Label>
                    <Form.Control
                      size="sm"
                      style={{ maxWidth: 160 }}
                      value={confirmText}
                      onChange={(e) => setConfirmText(e.target.value)}
                    />
                  </Form.Group>
                )}
              </div>
            )}
          </>
        )}
        {err && <Alert variant="danger" className="mt-3 small">{err}</Alert>}
      </Modal.Body>
      <Modal.Footer>
        <Button size="sm" variant="outline-secondary" onClick={close}>
          {run ? "Close" : "Cancel"}
        </Button>
        {!preview && (
          <Button size="sm" variant="primary" disabled={!canPreview || busy} onClick={stage}>
            {busy && <Spinner animation="border" size="sm" className="me-1" />}
            Preview
          </Button>
        )}
        {preview && !run && (
          <Button size="sm" variant="danger" disabled={!canApply || busy} onClick={apply}>
            {busy && <Spinner animation="border" size="sm" className="me-1" />}
            Apply to {queued} row{queued === 1 ? "" : "s"}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

const DomainSuggestions: React.FC<{
  suggestions: IDomainReviewBrandSuggestions[] | null;
  selected: string;
  onPick: (domain: string) => void;
}> = ({ suggestions, selected, onPick }) => {
  if (suggestions === null) {
    return (
      <div className="small text-muted mt-2">
        <Spinner animation="border" size="sm" className="me-1" /> Looking up the brand's domains…
      </div>
    );
  }
  if (suggestions.every((b) => b.suggestions.length === 0)) {
    return <div className="small text-muted mt-2">The selected brand has no other campaign domains to suggest.</div>;
  }
  return (
    <div className="mt-2">
      {suggestions.length > 1 && (
        <Alert variant="warning" className="small py-1 px-2 mb-2">
          The selected rows belong to {suggestions.length} brands; one replacement applies to all of them.
        </Alert>
      )}
      {suggestions
        .filter((b) => b.suggestions.length > 0)
        .map((b) => (
          <div key={b.brandId} className="mb-2">
            <div className="small fw-semibold mb-1">
              Suggested from {b.brandName ?? b.brandId}'s campaign domains (most validated first)
            </div>
            <div className="d-flex flex-wrap gap-1">
              {b.suggestions.map((sug) => (
                <Button
                  key={sug.domain}
                  size="sm"
                  variant={sug.domain === selected ? "primary" : "outline-secondary"}
                  onClick={() => onPick(sug.domain)}
                  title={`${sug.rows} campaign_domains row${sug.rows === 1 ? "" : "s"} for this brand: ${sug.validated} validated, ${sug.live} on live campaigns.`}
                >
                  {sug.domain}
                  <span className="ms-1 opacity-75" style={{ fontSize: 11 }}>
                    {sug.validated} validated · {sug.live} live
                  </span>
                </Button>
              ))}
            </div>
          </div>
        ))}
    </div>
  );
};

const ItemsTable: React.FC<{ items: IDomainReviewItem[] }> = ({ items }) => (
  <div style={{ maxHeight: "50vh", overflowY: "auto" }}>
    <Table size="sm" className="small align-middle mb-0">
      <thead>
        <tr>
          <th>Type</th>
          <th>Row</th>
          <th>Brand</th>
          <th>What happens</th>
          <th>Status</th>
        </tr>
      </thead>
      <tbody>
        {items.map((i) => (
          <tr key={i.rowNumber}>
            <td>{ENTITY_LABEL[i.entity]}</td>
            <td className="text-truncate" style={{ maxWidth: 220 }} title={i.entityId}>
              {i.label}
            </td>
            <td>{i.brandName ?? i.brandId ?? "—"}</td>
            <td>
              <div>{i.message && i.status !== "pending" ? i.message : i.plan}</div>
              {i.warnings.map((w) => (
                <div key={w} className="text-warning-emphasis">
                  <AlertTriangle size={12} className="me-1" />
                  {w}
                </div>
              ))}
            </td>
            <td>
              <Badge bg={ITEM_STATUS[i.status].variant} text={i.status === "no_change" ? "dark" : undefined}>
                {ITEM_STATUS[i.status].label}
              </Badge>
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  </div>
);

// ── History ──────────────────────────────────────────────────────────────────

const RunsCard: React.FC<{
  runs: IDomainReviewRunSummary[];
  onOpen: (runId: string) => void;
}> = ({ runs, onOpen }) => (
  <Card className="mb-3">
    <Card.Header className="py-2 small fw-semibold">
      Changes made on this domain
      <span className="text-muted fw-normal ms-2">Each keeps the before/after of every row it touched.</span>
    </Card.Header>
    <Card.Body className="p-0">
      {runs.length === 0 ? (
        <div className="text-muted small text-center py-3">No changes yet.</div>
      ) : (
        <Table size="sm" hover className="mb-0 small align-middle">
          <thead>
            <tr>
              <th>When</th>
              <th>Action</th>
              <th>By</th>
              <th>Reason</th>
              <th>Status</th>
              <th className="text-end">Updated / skipped / failed</th>
            </tr>
          </thead>
          <tbody>
            {runs.map((r) => (
              <tr key={r.runId} role="button" onClick={() => onOpen(r.runId)}>
                <td>{new Date(r.stagedAt).toLocaleString()}</td>
                <td>
                  {ACTIONS.find((a) => a.action === r.action)?.label.replace("…", "")}
                  {r.newDomain && ` → ${r.newDomain}`}
                  {r.newBrandId && ` → ${r.newBrandName ?? r.newBrandId}`}
                </td>
                <td>{r.triggeredByName ?? r.triggeredBy}</td>
                <td className="text-truncate" style={{ maxWidth: 240 }} title={r.reason ?? ""}>
                  {r.reason ?? "—"}
                </td>
                <td>{r.status}</td>
                <td className="text-end">
                  {r.counts.updated} / {r.counts.skipped} / {r.counts.failed}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Card.Body>
  </Card>
);

const RunDetailModal: React.FC<{ runId: string | null; onHide: () => void }> = ({ runId, onHide }) => {
  const [items, setItems] = useState<IDomainReviewItem[]>([]);
  const { execute, loading } = useGetLazy<IDomainReviewItemsResponse>();
  useEffect(() => {
    if (!runId) return;
    (async () => {
      const all: IDomainReviewItem[] = [];
      for (let page = 1; ; page++) {
        const r = await execute(`${BASE}/runs/${runId}/items?pageSize=500&page=${page}`);
        all.push(...r.items);
        if (all.length >= r.total || r.items.length === 0) break;
      }
      setItems(all);
    })().catch(() => setItems([]));
  }, [runId, execute]);
  return (
    <Modal show={!!runId} onHide={onHide} size="xl">
      <Modal.Header closeButton>
        <Modal.Title className="h6">Change {runId}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? <Spinner animation="border" size="sm" /> : <ItemsTable items={items} />}
        <details className="mt-3 small">
          <summary>Before / after values</summary>
          <pre className="small mb-0" style={{ maxHeight: 300, overflow: "auto" }}>
            {JSON.stringify(
              items
                .filter((i) => i.before || i.after)
                .map((i) => ({ row: i.label, entity: i.entity, id: i.entityId, before: i.before, after: i.after })),
              null,
              2
            )}
          </pre>
        </details>
      </Modal.Body>
    </Modal>
  );
};
